import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { auth } from "../../firebase";
import WorkspaceSidebar from "./WorkspaceSidebar";
import "../../css/WorkspaceContentDuplicateAnalysis.css";
import "../../css/DocumentationHomeComponent.css";

function WorkspaceContentDuplicateAnalysis() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [triggering, setTriggering] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [jobDetails, setJobDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [error, setError] = useState(null);
    const [uid, setUid] = useState(null);
    const [pollingJobId, setPollingJobId] = useState(null);
    const [workspace, setWorkspace] = useState(null);
    const navigate = useNavigate();
    const { customDomain } = useParams();

    // Auth listener
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
            } else {
                navigate("/login");
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    // Fetch workspace info
    useEffect(() => {
        if (uid && customDomain) {
            fetchWorkspaceInfo();
        }
    }, [uid, customDomain]);

    // Fetch jobs list
    useEffect(() => {
        if (uid && workspace) {
            fetchJobs();
        }
    }, [uid, workspace]);

    // Poll for running jobs
    useEffect(() => {
        if (pollingJobId && workspace) {
            const interval = setInterval(() => {
                fetchJobDetails(pollingJobId);
            }, 3000);

            return () => clearInterval(interval);
        }
    }, [pollingJobId, workspace]);

    const fetchWorkspaceInfo = async () => {
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/domain/${customDomain}/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                        "Auth-Token": `Bearer ${token}`,
                        "Requesting-User-Uid": uid,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setWorkspace(data);
            } else {
                setError("Failed to load workspace information");
            }
        } catch (err) {
            console.error("Error fetching workspace info:", err);
            setError("Error loading workspace");
        }
    };

    const fetchJobs = async () => {
        try {
            setLoading(true);
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/analyze-content-duplicates/jobs/`,
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setJobs(data.jobs || []);

                // Check if any job is running
                const runningJob = data.jobs?.find(
                    (job) => job.status === "pending" || job.status === "running"
                );
                if (runningJob) {
                    setPollingJobId(runningJob.job_id);
                } else {
                    setPollingJobId(null);
                }
            } else {
                setError("Failed to load analysis jobs");
            }
        } catch (err) {
            console.error("Error fetching jobs:", err);
            setError("Error loading jobs");
        } finally {
            setLoading(false);
        }
    };

    const fetchJobDetails = async (jobId) => {
        try {
            setLoadingDetails(true);
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/analyze-content-duplicates/jobs/${jobId}/`,
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setJobDetails(data);

                // Stop polling if job is no longer running
                if (data.status !== "pending" && data.status !== "running") {
                    setPollingJobId(null);
                    fetchJobs(); // Refresh the jobs list
                }
            } else {
                setError("Failed to load job details");
            }
        } catch (err) {
            console.error("Error fetching job details:", err);
            setError("Error loading job details");
        } finally {
            setLoadingDetails(false);
        }
    };

    const triggerAnalysis = async () => {
        try {
            setTriggering(true);
            setError(null);
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/analyze-content-duplicates/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setPollingJobId(data.job_id);
                fetchJobs();
            } else {
                const errorData = await response.json();
                setError(errorData.error || "Failed to start analysis");
            }
        } catch (err) {
            console.error("Error triggering analysis:", err);
            setError("Error starting analysis");
        } finally {
            setTriggering(false);
        }
    };

    const deleteJob = async (jobId) => {
        if (!window.confirm("Are you sure you want to delete this analysis job?")) {
            return;
        }

        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/analyze-content-duplicates/jobs/${jobId}/delete/`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );

            if (response.ok) {
                // Refresh jobs list and clear details if this job was selected
                if (selectedJob === jobId) {
                    setSelectedJob(null);
                    setJobDetails(null);
                }
                fetchJobs();
            } else {
                const errorData = await response.json();
                setError(errorData.error || "Failed to delete job");
            }
        } catch (err) {
            console.error("Error deleting job:", err);
            setError("Error deleting job");
        }
    };

    const handleJobClick = (jobId) => {
        setSelectedJob(jobId);
        fetchJobDetails(jobId);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            pending: "WorkspaceContentDuplicateAnalysis__status-pending",
            running: "WorkspaceContentDuplicateAnalysis__status-running",
            completed: "WorkspaceContentDuplicateAnalysis__status-completed",
            failed: "WorkspaceContentDuplicateAnalysis__status-failed",
        };

        return (
            <span className={`WorkspaceContentDuplicateAnalysis__status-badge ${statusClasses[status] || ""}`}>
                {status.toUpperCase()}
            </span>
        );
    };

    const getDocumentUrl = (docType, docId, docName) => {
        // Construct workspace document URL
        if (docType === "space") {
            return `/workspace/${customDomain}/space/${docId}`;
        } else if (docType === "page") {
            return `/workspace/${customDomain}/page/${docId}`;
        } else if (docType === "walkthrough") {
            return `/workspace/${customDomain}/walkthrough/${docId}`;
        }
        return "#";
    };

    const renderResults = () => {
        if (!jobDetails || !jobDetails.results) return null;

        const { results } = jobDetails;

        return (
            <div className="WorkspaceContentDuplicateAnalysis__results-container">
                <h3>Analysis Results</h3>
                <div className="WorkspaceContentDuplicateAnalysis__results-summary">
                    <div className="WorkspaceContentDuplicateAnalysis__summary-item">
                        <strong>Documents Analyzed:</strong> {results.total_documents_analyzed || 0}
                    </div>
                    <div className="WorkspaceContentDuplicateAnalysis__summary-item">
                        <strong>Duplicates Found:</strong> {results.total_duplicates_found || 0}
                    </div>
                </div>

                {results.duplicates && results.duplicates.length > 0 ? (
                    <div className="WorkspaceContentDuplicateAnalysis__duplicates-list">
                        <h4>Duplicate Content Details</h4>
                        {results.duplicates.map((dup, index) => (
                            <div key={index} className="WorkspaceContentDuplicateAnalysis__duplicate-item">
                                <div className="WorkspaceContentDuplicateAnalysis__duplicate-header">
                                    <strong>Duplicate Set #{index + 1}</strong>
                                    <span className="WorkspaceContentDuplicateAnalysis__similarity-score">
                                        Similarity: {(dup.similarity_score * 100).toFixed(1)}%
                                    </span>
                                </div>
                                <div className="WorkspaceContentDuplicateAnalysis__duplicate-documents">
                                    <div className="WorkspaceContentDuplicateAnalysis__document-info">
                                        <strong>Document 1:</strong>
                                        <a
                                            href={getDocumentUrl(
                                                dup.document_1.document_type,
                                                dup.document_1.id,
                                                dup.document_1.name
                                            )}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="WorkspaceContentDuplicateAnalysis__document-link"
                                        >
                                            {dup.document_1.name} ({dup.document_1.document_type})
                                        </a>
                                    </div>
                                    <div className="WorkspaceContentDuplicateAnalysis__document-info">
                                        <strong>Document 2:</strong>
                                        <a
                                            href={getDocumentUrl(
                                                dup.document_2.document_type,
                                                dup.document_2.id,
                                                dup.document_2.name
                                            )}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="WorkspaceContentDuplicateAnalysis__document-link"
                                        >
                                            {dup.document_2.name} ({dup.document_2.document_type})
                                        </a>
                                    </div>
                                </div>
                                {dup.content_preview_1 && (
                                    <div className="WorkspaceContentDuplicateAnalysis__duplicate-content">
                                        <strong>Duplicate Content Preview:</strong>
                                        <pre>{dup.content_preview_1.substring(0, 200)}...</pre>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="WorkspaceContentDuplicateAnalysis__no-duplicates">
                        <p>No duplicate content found in workspace documents. Great job keeping your documentation clean!</p>
                    </div>
                )}
            </div>
        );
    };

    const hasRunningJob = jobs.some(
        (job) => job.status === "pending" || job.status === "running"
    );

    if (!workspace) {
        return (
            <div className="DocumentationHomeComponent__coffee-loader-div">
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>Loading workspace...</p>
            </div>
        );
    }

    return (
        <div className="WorkspaceContentDuplicateAnalysis__page">
            <WorkspaceSidebar
                workspaceId={workspace.id}
                customDomain={customDomain}
                workspaceTheme={workspace.theme || "Default"}
            />
            <div className="WorkspaceContentDuplicateAnalysis__container">
                <div className="WorkspaceContentDuplicateAnalysis__page-header">
                    <h1>Content Duplicate Analysis</h1>
                    <p className="WorkspaceContentDuplicateAnalysis__page-description">
                        Analyze your workspace documentation to find duplicate or similar content across all documents, pages, and walkthroughs.
                    </p>
                    <p className="WorkspaceContentDuplicateAnalysis__workspace-name">
                        <strong>Workspace:</strong> {workspace.name}
                    </p>
                </div>

                {error && <div className="WorkspaceContentDuplicateAnalysis__error-message">{error}</div>}

                <div className="WorkspaceContentDuplicateAnalysis__action-section">
                    <button
                        onClick={triggerAnalysis}
                        disabled={triggering || hasRunningJob}
                        className="WorkspaceContentDuplicateAnalysis__trigger-button"
                    >
                        {triggering ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i> Starting Analysis...
                            </>
                        ) : hasRunningJob ? (
                            "Analysis In Progress..."
                        ) : (
                            <>
                                <i className="fas fa-search"></i> Start New Analysis
                            </>
                        )}
                    </button>
                    {hasRunningJob && (
                        <p className="WorkspaceContentDuplicateAnalysis__info-text">
                            An analysis is currently running. Results will appear when complete.
                        </p>
                    )}
                </div>

                <div className="WorkspaceContentDuplicateAnalysis__content-layout">
                    <div className="WorkspaceContentDuplicateAnalysis__jobs-section">
                        <h2>Analysis History</h2>
                        {loading ? (
                            <div className="WorkspaceContentDuplicateAnalysis__loading-spinner">
                                <i className="fas fa-spinner fa-spin"></i> Loading...
                            </div>
                        ) : jobs.length === 0 ? (
                            <p className="WorkspaceContentDuplicateAnalysis__no-jobs">No analysis jobs yet. Start your first analysis above!</p>
                        ) : (
                            <div className="WorkspaceContentDuplicateAnalysis__jobs-list">
                                {jobs.map((job) => (
                                    <div
                                        key={job.job_id}
                                        className={`WorkspaceContentDuplicateAnalysis__job-card ${selectedJob === job.job_id ? "WorkspaceContentDuplicateAnalysis__selected" : ""}`}
                                        onClick={() => handleJobClick(job.job_id)}
                                    >
                                        <div className="WorkspaceContentDuplicateAnalysis__job-header">
                                            <span className="WorkspaceContentDuplicateAnalysis__job-id">Job #{job.job_id}</span>
                                            {getStatusBadge(job.status)}
                                        </div>
                                        <div className="WorkspaceContentDuplicateAnalysis__job-meta">
                                            <div className="WorkspaceContentDuplicateAnalysis__job-date">
                                                <i className="far fa-clock"></i> {formatDate(job.created_at)}
                                            </div>
                                            {job.created_by && (
                                                <div className="WorkspaceContentDuplicateAnalysis__job-creator">
                                                    <i className="fas fa-user"></i> {job.created_by.name || job.created_by.email}
                                                </div>
                                            )}
                                            {job.summary && (
                                                <div className="WorkspaceContentDuplicateAnalysis__job-summary">
                                                    <span>{job.summary.total_documents_analyzed} docs analyzed</span>
                                                    <span className="WorkspaceContentDuplicateAnalysis__separator">•</span>
                                                    <span>{job.summary.total_duplicates_found} duplicates</span>
                                                </div>
                                            )}
                                        </div>
                                        {job.status !== "pending" && job.status !== "running" && (
                                            <button
                                                className="WorkspaceContentDuplicateAnalysis__delete-job-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deleteJob(job.job_id);
                                                }}
                                                title="Delete job"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="WorkspaceContentDuplicateAnalysis__details-section">
                        {selectedJob ? (
                            <>
                                {loadingDetails ? (
                                    <div className="WorkspaceContentDuplicateAnalysis__loading-spinner">
                                        <i className="fas fa-spinner fa-spin"></i> Loading details...
                                    </div>
                                ) : jobDetails ? (
                                    <div className="WorkspaceContentDuplicateAnalysis__job-details">
                                        <div className="WorkspaceContentDuplicateAnalysis__details-header">
                                            <h2>Job #{jobDetails.job_id} Details</h2>
                                            {getStatusBadge(jobDetails.status)}
                                        </div>
                                        <div className="WorkspaceContentDuplicateAnalysis__details-meta">
                                            <div className="WorkspaceContentDuplicateAnalysis__meta-item">
                                                <strong>Created:</strong> {formatDate(jobDetails.created_at)}
                                            </div>
                                            {jobDetails.started_at && (
                                                <div className="WorkspaceContentDuplicateAnalysis__meta-item">
                                                    <strong>Started:</strong> {formatDate(jobDetails.started_at)}
                                                </div>
                                            )}
                                            {jobDetails.completed_at && (
                                                <div className="WorkspaceContentDuplicateAnalysis__meta-item">
                                                    <strong>Completed:</strong> {formatDate(jobDetails.completed_at)}
                                                </div>
                                            )}
                                        </div>

                                        {jobDetails.status === "failed" && jobDetails.error_message && (
                                            <div className="WorkspaceContentDuplicateAnalysis__error-details">
                                                <strong>Error:</strong>
                                                <p>{jobDetails.error_message}</p>
                                            </div>
                                        )}

                                        {jobDetails.status === "completed" && renderResults()}

                                        {(jobDetails.status === "pending" || jobDetails.status === "running") && (
                                            <div className="WorkspaceContentDuplicateAnalysis__processing-info">
                                                <i className="fas fa-spinner fa-spin"></i>
                                                <p>Analysis is {jobDetails.status}. This may take a few minutes...</p>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <div className="WorkspaceContentDuplicateAnalysis__no-selection">
                                <i className="fas fa-hand-pointer"></i>
                                <p>Select a job from the list to view details</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WorkspaceContentDuplicateAnalysis;
